import { Container, Navbar, Nav, Dropdown, Modal, Form, Alert } from 'react-bootstrap';
// New package button
import { Button } from '@veeyaainnovatives/button';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { customerApi } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/api';
import { useState, useEffect, useRef } from 'react';
import logo from '../../assets/images/sds-logo.svg';

const CustomerLayout = ({ children }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [showDropdown, setShowDropdown] = useState(false);
    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showDropdown]);

    const handleLogout = async () => {
        try {
            await logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Always redirect to login, even if API call fails
            navigate('/customer/login');
        }
    };

    const handleResetPassword = () => {
        setShowResetPasswordModal(true);
        setShowDropdown(false);
    };

    const handleCloseResetPasswordModal = () => {
        setShowResetPasswordModal(false);
        setFormData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setErrors({});
        setSubmitMessage({ type: '', text: '' });
    };

    const validateForm = () => {
        let newErrors = {};

        if (!formData.currentPassword.trim()) {
            newErrors.currentPassword = 'Current password is required.';
        }

        if (!formData.newPassword.trim()) {
            newErrors.newPassword = 'New password is required.';
        } else if (formData.newPassword.length < 6) {
            newErrors.newPassword = 'Password must be at least 6 characters.';
        }

        if (!formData.confirmPassword.trim()) {
            newErrors.confirmPassword = 'Please confirm your new password.';
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match.';
        }

        if (formData.currentPassword === formData.newPassword && formData.newPassword) {
            newErrors.newPassword = 'New password must be different from current password.';
        }

        return newErrors;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }
        if (submitMessage.text) {
            setSubmitMessage({ type: '', text: '' });
        }
    };

    const handleSubmitResetPassword = async (e) => {
        e.preventDefault();

        const newErrors = validateForm();
        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            return;
        }

        setIsSubmitting(true);
        setSubmitMessage({ type: '', text: '' });
        setErrors({});

        try {
            const apiUrl = getApiUrl('api/customer/change-password');

            const response = await customerApi.post(
                apiUrl,
                {
                    oldPassword: formData.currentPassword,
                    newPassword: formData.newPassword,
                    confirmPassword: formData.confirmPassword
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setSubmitMessage({ 
                    type: 'success', 
                    text: response.data.message || 'Password changed successfully! Please login again with your new password.' 
                });
                
                // Clear form
                setFormData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                
                // Auto close after 2 seconds and logout
                setTimeout(() => {
                    handleCloseResetPasswordModal();
                    logout();
                    navigate('/customer/login');
                }, 2000);
            } else {
                setSubmitMessage({ 
                    type: 'danger', 
                    text: response.data.message || 'Failed to change password. Please try again.' 
                });
            }
        } catch (error) {
            console.error('Reset password API error:', error);
            
            if (error.response) {
                const { status, data } = error.response;
                console.error('API Error Response:', { status, data });
                
                // Validation errors (422)
                if (status === 422 && data.errors) {
                    // Map API error field names to form field names
                    const apiErrors = {};
                    if (data.errors.oldPassword) {
                        apiErrors.currentPassword = Array.isArray(data.errors.oldPassword) 
                            ? data.errors.oldPassword[0] 
                            : data.errors.oldPassword;
                    }
                    if (data.errors.newPassword) {
                        apiErrors.newPassword = Array.isArray(data.errors.newPassword) 
                            ? data.errors.newPassword[0] 
                            : data.errors.newPassword;
                    }
                    if (data.errors.confirmPassword) {
                        apiErrors.confirmPassword = Array.isArray(data.errors.confirmPassword) 
                            ? data.errors.confirmPassword[0] 
                            : data.errors.confirmPassword;
                    }
                    
                    setErrors(apiErrors);
                    
                    // Show general error message if available
                    if (data.message) {
                        setSubmitMessage({ 
                            type: 'danger', 
                            text: data.message 
                        });
                    } else {
                        // Combine all validation errors
                        const errorMessages = Object.values(apiErrors).join(', ');
                        setSubmitMessage({ 
                            type: 'danger', 
                            text: errorMessages || 'Validation error. Please check your inputs.' 
                        });
                    }
                } 
                // Unauthorized (401)
                else if (status === 401) {
                    setSubmitMessage({ 
                        type: 'danger', 
                        text: data.message || 'Session expired. Please login again.' 
                    });
                    // Auto logout and redirect after 2 seconds
                    setTimeout(() => {
                        handleCloseResetPasswordModal();
                        logout();
                        navigate('/customer/login');
                    }, 2000);
                } 
                // Other errors
                else {
                    setSubmitMessage({ 
                        type: 'danger', 
                        text: data.message || 'Failed to change password. Please try again.' 
                    });
                }
            } else if (error.request) {
                console.error('No response received:', error.request);
                setSubmitMessage({ 
                    type: 'danger', 
                    text: 'Network error. Please check your connection and try again.' 
                });
            } else {
                console.error('Error setting up request:', error.message);
                setSubmitMessage({ 
                    type: 'danger', 
                    text: 'An error occurred. Please try again.' 
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const isActive = (path) => {
        return location.pathname === path ? 'active' : '';
    };

    // Get first letter of user's name or username
    const getInitial = () => {
        const name = user?.name || user?.username || 'C';
        return name.charAt(0).toUpperCase();
    };

    return (
        <div className="customer-portal-wrapper d-flex flex-column min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
            {/* Customer Portal Header */}
            <Navbar expand="lg" className="shadow-sm" style={{ backgroundColor: '#677a58' }}>
                <Container fluid className="d-flex justify-content-between align-items-center">
                    <Navbar.Brand as={Link} to="/customer/dashboard" style={{ cursor: 'pointer' }}>
                        <img src={logo} alt="SDS Logo" height="40" />
                    </Navbar.Brand>
                    {/* Always show profile icon - no hamburger menu */}
                    <Nav className="align-items-center">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }} ref={dropdownRef}>
                                {/* Profile Circle */}
                                <Dropdown show={showDropdown} onToggle={setShowDropdown}>
                                    <Dropdown.Toggle 
                                        as="button"
                                        variant="link"
                                        className="p-0 border-0 customer-profile-toggle"
                                        style={{ 
                                            textDecoration: 'none',
                                            outline: 'none',
                                            boxShadow: 'none',
                                            padding: '0 !important',
                                            display: 'flex',
                                            alignItems: 'center',
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            background: 'none',
                                            width: 'auto',
                                            height: 'auto',
                                            minWidth: 'auto',
                                            minHeight: 'auto',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <div 
                                            className="customer-profile-circle"
                                            style={{
                                                width: '44px',
                                                height: '44px',
                                                borderRadius: '50%',
                                                backgroundColor: '#ffffff',
                                                color: '#677a58',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: '600',
                                                fontSize: '16px',
                                                cursor: 'pointer',
                                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                                transition: 'all 0.3s ease',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                                lineHeight: '1',
                                                userSelect: 'none'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'scale(1.08)';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                                                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'scale(1)';
                                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                                                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                                            }}
                                        >
                                            {getInitial()}
                                        </div>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu 
                                        align="end"
                                        className="customer-profile-dropdown"
                                        style={{
                                            borderRadius: '12px',
                                            border: 'none',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                            marginTop: '12px',
                                            minWidth: '220px',
                                            maxWidth: 'calc(100vw - 40px)',
                                            padding: '8px',
                                            overflow: 'hidden',
                                            right: '0',
                                            left: 'auto',
                                            position: 'absolute',
                                            zIndex: 1050
                                        }}
                                    >
                                        {/* Header with greeting */}
                                        <div className="customer-dropdown-header" style={{ 
                                            padding: '12px 16px',
                                            borderBottom: '1px solid #e9ecef',
                                            marginBottom: '4px'
                                        }}>
                                            <span style={{ 
                                                fontWeight: '600',
                                                color: '#212529',
                                                fontSize: '15px',
                                                wordBreak: 'break-word'
                                            }}>
                                                Hi {user?.name || user?.username || 'Customer'}
                                            </span>
                                        </div>
                                        
                                        {/* Reset Password Option */}
                                        <Dropdown.Item 
                                            onClick={handleResetPassword}
                                            className="customer-dropdown-item"
                                            style={{
                                                padding: '14px 16px',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s',
                                                borderRadius: '8px',
                                                margin: '2px 0',
                                                display: 'flex',
                                                alignItems: 'center',
                                                color: '#212529',
                                                fontSize: '14px',
                                                minHeight: '48px'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            onTouchStart={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                            onTouchEnd={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '10px', flexShrink: 0 }}>
                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                            </svg>
                                            <span>Reset Password</span>
                                        </Dropdown.Item>
                                        
                                        <Dropdown.Divider style={{ margin: '8px 0' }} />
                                        
                                        {/* Logout Option */}
                                        <Dropdown.Item 
                                            onClick={handleLogout}
                                            className="customer-dropdown-item"
                                            style={{
                                                padding: '14px 16px',
                                                cursor: 'pointer',
                                                color: '#dc3545',
                                                transition: 'background-color 0.2s',
                                                borderRadius: '8px',
                                                margin: '2px 0',
                                                display: 'flex',
                                                alignItems: 'center',
                                                fontSize: '14px',
                                                minHeight: '48px'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fff5f5'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            onTouchStart={(e) => e.currentTarget.style.backgroundColor = '#fff5f5'}
                                            onTouchEnd={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '10px', flexShrink: 0 }}>
                                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                                <polyline points="16 17 21 12 16 7"></polyline>
                                                <line x1="21" y1="12" x2="9" y2="12"></line>
                                            </svg>
                                            <span>Logout</span>
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        </Nav>
                </Container>
            </Navbar>

            {/* Main Content */}
            <main className="py-4 flex-grow-1">
                {children}
            </main>

            {/* Footer - Matching Reference Design */}
            <footer className="customer-portal-footer" style={{
                backgroundColor: '#677a58',
                padding: '24px 0',
                borderTop: '1px solid #e7b06d',
                textAlign: 'center',
                marginBottom: '0'
            }}>
                <Container>
                    <p style={{
                        margin: '0',
                        color: '#f5f5f5',
                        fontSize: '14px',
                        fontWeight: '400',
                        lineHeight: '1.5'
                    }}>
                        © 2025 Sathish Design Studio. All rights reserved | Designed & Developed by{' '}
                        <a 
                            href="https://veeyaainnovatives.com/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                                color: '#e7b06d',
                                textDecoration: 'none',
                                transition: 'color 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#f29a22'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#e7b06d'}
                        >
                            VeeyaaInnovatives
                        </a>
                    </p>
                </Container>
            </footer>

            {/* Reset Password Modal */}
            <Modal 
                show={showResetPasswordModal} 
                onHide={handleCloseResetPasswordModal}
                centered
                size="md"
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header style={{ 
                    backgroundColor: '#677a58', 
                    color: '#fff',
                    borderBottom: '1px solid #e7b06d'
                }}>
                    <Modal.Title style={{ 
                        fontSize: '20px', 
                        fontWeight: '600',
                        color: '#fff'
                    }}>
                        Reset Password
                    </Modal.Title>
                    <Button 
                        variant="text" 
                        onClick={handleCloseResetPasswordModal}
                        backgroundColor="transparent"
                        color="#ffffff"
                        hoverBackgroundColor="transparent"
                        padding="0"
                        style={{ marginLeft: 'auto', opacity: 0.8 }}
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </Button>
                </Modal.Header>
                <Modal.Body style={{ padding: '30px' }}>
                    {submitMessage.text && (
                        <Alert variant={submitMessage.type === 'success' ? 'success' : 'danger'} className="mb-3">
                            {submitMessage.text}
                        </Alert>
                    )}
                    
                    <Form onSubmit={handleSubmitResetPassword}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ 
                                fontWeight: '500', 
                                color: '#212529',
                                marginBottom: '8px'
                            }}>
                                Current Password
                            </Form.Label>
                            <Form.Control
                                type="password"
                                name="currentPassword"
                                value={formData.currentPassword}
                                onChange={handleInputChange}
                                isInvalid={!!errors.currentPassword}
                                style={{
                                    borderRadius: '8px',
                                    border: errors.currentPassword ? '1px solid #dc3545' : '1px solid #ced4da',
                                    padding: '12px',
                                    fontSize: '14px'
                                }}
                                placeholder="Enter current password"
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.currentPassword}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label style={{ 
                                fontWeight: '500', 
                                color: '#212529',
                                marginBottom: '8px'
                            }}>
                                New Password
                            </Form.Label>
                            <Form.Control
                                type="password"
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleInputChange}
                                isInvalid={!!errors.newPassword}
                                style={{
                                    borderRadius: '8px',
                                    border: errors.newPassword ? '1px solid #dc3545' : '1px solid #ced4da',
                                    padding: '12px',
                                    fontSize: '14px'
                                }}
                                placeholder="Enter new password (min 6 characters)"
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.newPassword}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label style={{ 
                                fontWeight: '500', 
                                color: '#212529',
                                marginBottom: '8px'
                            }}>
                                Confirm New Password
                            </Form.Label>
                            <Form.Control
                                type="password"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                isInvalid={!!errors.confirmPassword}
                                style={{
                                    borderRadius: '8px',
                                    border: errors.confirmPassword ? '1px solid #dc3545' : '1px solid #ced4da',
                                    padding: '12px',
                                    fontSize: '14px'
                                }}
                                placeholder="Confirm new password"
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.confirmPassword}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <div className="d-flex gap-2 justify-content-end">
                            <Button 
                                onClick={handleCloseResetPasswordModal}
                                disabled={isSubmitting}
                                backgroundColor="#6c757d"
                                color="#ffffff"
                                hoverBackgroundColor="#5a6268"
                                borderRadius="8px"
                                padding="10px 20px"
                                fontWeight="500"
                            >
                                Cancel
                            </Button>
                            <Button 
                                type="submit"
                                disabled={isSubmitting}
                                backgroundColor="#677a58"
                                color="#ffffff"
                                hoverBackgroundColor="#5a6a4a"
                                borderRadius="8px"
                                padding="10px 24px"
                                fontWeight="500"
                            >
                                {isSubmitting ? 'Resetting...' : 'Reset Password'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default CustomerLayout;

